/**
 * Web 端主题换肤：8 套品牌色系，与 Taro 小程序主题体系对齐。
 *
 * CSS 变量定义在 tokens.css 的 :root（默认赤陶砖红）与 .theme-xxx 上，
 * 切换时在 document.documentElement 上绑定 theme-{{themeKey}}，即时换肤。
 * storage key 沿用小程序 zj_theme，跨端用户已选主题可无缝继承。
 */

export interface ThemeMeta {
  key: string;
  name: string;
  tagline: string;
  /** 色卡主色（主题选择器展示用） */
  color: string;
  /** 深色主色（hover / 选中态） */
  colorStrong: string;
  /** 主色软底 */
  colorSoft: string;
}

export const THEMES: ThemeMeta[] = [
  {
    key: 'terracotta',
    name: '赤陶砖红',
    tagline: '日落 · 暖光 · 复古',
    color: '#b04a2e',
    colorStrong: '#8a3620',
    colorSoft: '#f5e6e0',
  },
  {
    key: 'caramel',
    name: '胶片焦糖',
    tagline: '暗房 · 胶片 · 温度',
    color: '#8c5a33',
    colorStrong: '#6b4222',
    colorSoft: '#f2e8dc',
  },
  {
    key: 'indigo',
    name: '深靛藏青',
    tagline: '夜空 · 克制 · 专业',
    color: '#2b3a67',
    colorStrong: '#1a2547',
    colorSoft: '#e8ebf2',
  },
  {
    key: 'forest',
    name: '松绿',
    tagline: '自然 · 骑行 · 克制',
    color: '#0e9f6e',
    colorStrong: '#145c42',
    colorSoft: '#e7f6ef',
  },
  {
    key: 'sunset',
    name: '暖阳橙',
    tagline: '阳光 · 活力 · 胶片',
    color: '#ef6424',
    colorStrong: '#c04410',
    colorSoft: '#fef3e8',
  },
  {
    key: 'ocean',
    name: '海盐蓝',
    tagline: '天空 · 风 · 自由',
    color: '#0f86d0',
    colorStrong: '#0b5c94',
    colorSoft: '#e8f4fd',
  },
  {
    key: 'amber',
    name: '胶片棕',
    tagline: '摄影 · 怀旧 · 温暖',
    color: '#a76b2e',
    colorStrong: '#774316',
    colorSoft: '#fbf4e8',
  },
  {
    key: 'graphite',
    name: '石墨蓝',
    tagline: '中性 · 精致 · 耐看',
    color: '#4853c4',
    colorStrong: '#2f3690',
    colorSoft: '#eef0fc',
  },
];

export type ThemeKey = (typeof THEMES)[number]['key'];

export const DEFAULT_THEME: ThemeKey = 'terracotta';

const THEME_KEYS = new Set(THEMES.map((t) => t.key));
const STORAGE_KEY = 'zj_theme';

export function normalizeTheme(key: unknown): ThemeKey {
  return typeof key === 'string' && THEME_KEYS.has(key) ? (key as ThemeKey) : DEFAULT_THEME;
}

export function getThemeMeta(key: ThemeKey): ThemeMeta {
  return THEMES.find((t) => t.key === key) ?? THEMES[0]!;
}

export function readStoredTheme(): ThemeKey {
  try {
    return normalizeTheme(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

export function writeStoredTheme(key: ThemeKey): void {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    /* 存储不可用时忽略 */
  }
}

/** 应用主题到 document.documentElement，移除旧主题 class，添加新主题 class */
export function applyTheme(key: ThemeKey): void {
  const el = document.documentElement;
  for (const t of THEMES) {
    el.classList.remove(`theme-${t.key}`);
  }
  el.classList.add(`theme-${key}`);
}

/** 初始化主题：从 localStorage 读取并应用（应在应用启动早期调用，避免闪烁） */
export function initTheme(): ThemeKey {
  const theme = readStoredTheme();
  applyTheme(theme);
  return theme;
}

/** 切换主题：更新 DOM class + 持久化存储，返回新主题 key */
export function setTheme(raw: ThemeKey | string): ThemeKey {
  const theme = normalizeTheme(raw);
  applyTheme(theme);
  writeStoredTheme(theme);
  return theme;
}
